/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { IApiQueryOptions } from 'api/ApiClient';
import { DateRangePicker, Empty, EmptyTable, FilterSelector, Label, PaginatedList, SectionHeader } from 'components';
import { IFilterSelectorOption } from 'components/FilterSelector/FilterSelector';
import { useQueryString } from 'hooks';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getSiteTipAcceptancePaymentSetsRequest, Payments, selectSiteTipAcceptancePaymentSets } from 'store/Collections';
import { selectSiteAcceptance, selectSiteAcceptanceBalance } from 'store/Site';
import { IStore } from 'store/types';
import { ApiQueryOperator, ITipAcceptancePaymentSet } from 'types';
import { ContentHeader } from './components/styles';
import TipAcceptancePaymentSet from './components/TipAcceptancePaymentSet';

const MENU_OPTIONS: IFilterSelectorOption[] = [
  { value: 'pending', label: 'Payments to Review', emptyMessage: 'There are no payments to review' },
  { value: 'completed', label: 'Completed Payments', emptyMessage: 'There are no completed payments for the selected date range' },
];

interface IParams {
  option?: string;
}

const TipAcceptancePayments = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { option = 'pending' } = useParams<IParams>();
  const [urlQuery, setUrlQuery] = useQueryString('tipAcceptancePayments', window.location, {
    start_date: dayjs().subtract(6, 'days').format('MM-DD-YYYY'),
    end_date: dayjs().endOf('day').format('MM-DD-YYYY'),
  });

  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentOrganizationId = currentEmployee?.organizationId || '';
  const currentSiteId = currentEmployee?.siteId || '';
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const acceptanceBalance = useSelector(selectSiteAcceptanceBalance());

  const paymentSets = useSelector(selectSiteTipAcceptancePaymentSets());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_TIP_ACCEPTANCE_PAYMENT_SETS_REQUEST));

  const onQueryChange = (options: IApiQueryOptions) => {
    setUrlQuery(options);
  };

  useEffect(() => {
    if (currentSiteId && currentOrganizationId && currentSiteAcceptance?.enabled) {
      const { start_date, end_date, limit, offset } = urlQuery;
      dispatch(
        getSiteTipAcceptancePaymentSetsRequest(currentSiteId, {
          q: [
            { key: 'payment_date', operator: ApiQueryOperator.gte, value: dayjs(start_date, 'MM-DD-YYYY').format('YYYY-MM-DD') },
            { key: 'payment_date', operator: ApiQueryOperator.lte, value: dayjs(end_date, 'MM-DD-YYYY').format('YYYY-MM-DD') },
            { key: 'status', operator: ApiQueryOperator.eq, value: option },
          ],
          sort: '-payment_date',
          ...(limit && { limit }),
          ...(offset && { offset }),
        })
      );
    }
  }, [dispatch, currentSiteId, currentOrganizationId, currentSiteAcceptance, option, urlQuery]);

  const onFilterChange = (filter: IFilterSelectorOption[]) => {
    if (urlQuery.offset) {
      // clear offset to reset to first page
      setUrlQuery({ offset: undefined });
    }

    history.push({
      pathname: `${routeConstants.private.paymentsAcceptance}/${filter[0].value}`,
      search: window.location.search,
    });
  };

  const onDateRangeChange = (dates: string[]) => {
    setUrlQuery({ start_date: dayjs(dates[0]).format('MM-DD-YYYY'), end_date: dayjs(dates[1]).format('MM-DD-YYYY'), offset: undefined });
  };

  const getMakePaymentUrl = useCallback((paymentSet: ITipAcceptancePaymentSet) => `${routeConstants.private.makePayment}/acceptance/${currentSiteId}/${paymentSet.id}`, [currentSiteId]);

  return (
    <>
      <ContentHeader>
        {!isLoading && (
          <SectionHeader title={<FilterSelector options={MENU_OPTIONS} onChange={onFilterChange} selectedValue={option} width={210} />} data-cy="acceptance-payments-content-header">
            <>
              <Label>Business Days</Label>
              <DateRangePicker fluidWidth={false} inputHeight="small" onSubmit={onDateRangeChange} pickerPosition="right" startDate={urlQuery.start_date} endDate={urlQuery.end_date} submitLabel="apply" />
            </>
          </SectionHeader>
        )}
      </ContentHeader>

      {!isLoading && paymentSets?.total === 0 && (
        <EmptyTable>
          <Empty icon="Payment" message={MENU_OPTIONS.find((opt) => opt.value === option)?.emptyMessage || 'No payments found.'} />
        </EmptyTable>
      )}

      {isLoading || !paymentSets ? (
        <TipAcceptancePaymentSet isLoading />
      ) : (
        <PaginatedList total={paymentSets?.total} limit={paymentSets?.limit} offset={paymentSets?.offset} onPageChange={onQueryChange} isLoading={isLoading || !paymentSets?.total}>
          {paymentSets?.data.map((paymentSet: ITipAcceptancePaymentSet) => (
            <TipAcceptancePaymentSet key={paymentSet.id} isLoading={isLoading} acceptanceBalance={acceptanceBalance} acceptancePaymentSet={paymentSet} makePaymentUrl={getMakePaymentUrl(paymentSet)} summaryType={option} />
          ))}
        </PaginatedList>
      )}
    </>
  );
};

export default TipAcceptancePayments;
